import React, { useRef, useState, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { Citation } from './types';
import { getClauseDisplayTitle } from '../../utils/clauseDisplay';

interface CitationTooltipProps {
  citation: Citation;
  visible: boolean;
  children: React.ReactNode;
}

const TOOLTIP_WIDTH = 256;
const TOOLTIP_MARGIN = 8;
const VIEWPORT_PADDING = 16;

const CitationTooltip: React.FC<CitationTooltipProps> = ({
  citation,
  visible,
  children
}) => {
  const triggerRef = useRef<HTMLSpanElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0, arrowLeft: '50%' });

  // Calculate position when visible changes or on scroll/resize
  useLayoutEffect(() => {
    if (!visible || !triggerRef.current) return;

    const calculatePosition = () => {
      const trigger = triggerRef.current;
      if (!trigger) return;

      const rect = trigger.getBoundingClientRect();
      const viewportWidth = window.innerWidth;

      // Center the tooltip on the trigger by default
      let left = rect.left + rect.width / 2 - TOOLTIP_WIDTH / 2;

      // Check right overflow
      if (left + TOOLTIP_WIDTH > viewportWidth - VIEWPORT_PADDING) {
        left = viewportWidth - VIEWPORT_PADDING - TOOLTIP_WIDTH;
      }

      // Check left overflow
      if (left < VIEWPORT_PADDING) {
        left = VIEWPORT_PADDING;
      }

      // Calculate arrow position (should point to center of trigger)
      const triggerCenter = rect.left + rect.width / 2;
      const arrowLeftPx = triggerCenter - left;
      // Clamp arrow to stay within tooltip bounds (with some padding)
      const clampedArrowLeft = Math.max(16, Math.min(arrowLeftPx, TOOLTIP_WIDTH - 16));

      setPosition({
        top: rect.top - TOOLTIP_MARGIN + window.scrollY,
        left: left + window.scrollX,
        arrowLeft: `${clampedArrowLeft}px`,
      });
    };

    calculatePosition();

    // Recalculate on scroll/resize
    window.addEventListener('scroll', calculatePosition, true);
    window.addEventListener('resize', calculatePosition);

    return () => {
      window.removeEventListener('scroll', calculatePosition, true);
      window.removeEventListener('resize', calculatePosition);
    };
  }, [visible]);

  // Plan 159 — branch on source_type for the hover label. Legacy
  // pre-159 citations have no source_type; treat undefined as 'matter'
  // so historical sessions render unchanged.
  //
  // Matter:     "<document_name> · p.<N>" (existing)
  // Workbench:  "Precedent: <title> · <logical_id>" (new)
  const effectiveType = citation.source_type ?? 'matter';
  const pageNum: number | null | undefined =
    (citation as any).page_number ?? (citation as any).page;
  let docLine: string;
  if (effectiveType === 'workbench') {
    docLine = [
      citation.workbench_document_title
        ? `Precedent: ${citation.workbench_document_title}`
        : 'Precedent',
      citation.logical_id ?? citation.clause_id ?? null,
    ]
      .filter(Boolean)
      .join(' · ');
  } else {
    docLine = [
      citation.document_name,
      pageNum ? `p.${pageNum}` : null,
    ]
      .filter(Boolean)
      .join(' · ');
  }

  // Excerpt: collapse whitespace, skip leading partial-word/partial-sentence,
  // truncate at a word boundary so the tooltip can't grow tall on chunked text
  // that starts mid-paragraph or contains many newlines.
  const truncateExcerpt = (text: string | undefined, maxLength = 220): string => {
    if (!text) return '';
    let cleaned = text.replace(/\s+/g, ' ').trim();
    if (!cleaned) return '';
    // Skip past partial leading word / mid-sentence chunk start.
    if (/^[a-z]/.test(cleaned) || /^\S*[;,.]/.test(cleaned.split(' ')[0])) {
      const sentenceStart = cleaned.search(/[.;!?]\s+[A-Z(]/);
      if (sentenceStart !== -1) cleaned = cleaned.substring(sentenceStart + 2);
    }
    if (cleaned.length <= maxLength) return cleaned;
    const truncated = cleaned.substring(0, maxLength);
    const lastSpace = truncated.lastIndexOf(' ');
    return (lastSpace > 0 ? truncated.substring(0, lastSpace) : truncated) + '…';
  };
  const excerpt = truncateExcerpt(citation.excerpt);

  const tooltipContent = visible && (
    <div
      role="tooltip"
      style={{
        position: 'absolute',
        top: position.top,
        left: position.left,
        transform: 'translateY(-100%)',
        width: `${TOOLTIP_WIDTH}px`,
        zIndex: 9999,
        background: '#1f2937',
        color: '#ffffff',
        borderRadius: 8,
        padding: '8px 12px',
        boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -4px rgba(0,0,0,0.1)',
        fontSize: 14,
      }}
    >
      {/* Tooltip arrow */}
      <div
        data-tooltip-arrow
        style={{
          position: 'absolute',
          top: '100%',
          left: position.arrowLeft,
          transform: 'translateX(-50%)',
          width: 0,
          height: 0,
          borderLeft: '4px solid transparent',
          borderRight: '4px solid transparent',
          borderTop: '4px solid #1f2937',
        }}
      />

      {/* Compact citation card — heading on top, doc · page on the second line.
          Inline styles only — Tailwind class purging on long-running CRA dev
          builds was eating utility classes here. Issue #821: title is now
          always rendered via getClauseDisplayTitle so marker-only clauses
          show "Clause N" instead of being silently omitted. */}
      <div style={{ fontWeight: 600, lineHeight: 1.3 }}>
        {getClauseDisplayTitle(citation.section_heading, citation.logical_id)}
      </div>
      {docLine && (
        <div
          style={{
            fontSize: 12,
            color: '#d1d5db',
            lineHeight: 1.3,
            marginTop: 4,
            fontWeight: 'normal',
            wordBreak: 'break-word',
          }}
        >
          {docLine}
        </div>
      )}
      {excerpt && (
        <div
          style={{
            fontSize: 12,
            color: '#e5e7eb',
            lineHeight: 1.5,
            marginTop: 6,
            fontStyle: 'italic',
            wordBreak: 'break-word',
          }}
        >
          {excerpt}
        </div>
      )}
    </div>
  );

  return (
    <>
      <span ref={triggerRef} className="inline">
        {children}
      </span>
      {visible && createPortal(tooltipContent, document.body)}
    </>
  );
};

export default CitationTooltip;
